import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, ArrowLeft, Send } from "lucide-react";
import { formatApiError } from "@/lib/api";
import portalApi from "@/lib/portalApi";
import { Pill, TICKET_STATUS, PRIORITIES, fmtDate } from "@/components/portal/portalUi";
import { Empty } from "@/components/portal/QuotesTab";

const field = "w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 text-sm focus:border-[#0F2A6B] focus:outline-none";

function NewTicket({ onCreated, onCancel }) {
  const [f, setF] = useState({ subject: "", category: "assistance", priority: "normale", message: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await portalApi.post("/portal/tickets", f); toast.success(`Ticket ${data.reference} créé`); onCreated(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <form onSubmit={submit} className="p-6 rounded-2xl bg-white border border-slate-200 space-y-4" data-testid="portal-ticket-form">
      <input required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Sujet de votre demande" className={field} data-testid="portal-ticket-subject" />
      <div className="grid sm:grid-cols-2 gap-4">
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={field} data-testid="portal-ticket-category">
          <option value="assistance">Assistance technique</option><option value="logiciel">Logiciel / bug</option><option value="materiel">Matériel</option><option value="facturation">Facturation</option><option value="autre">Autre</option>
        </select>
        <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className={field} data-testid="portal-ticket-priority">
          {Object.entries(PRIORITIES).map(([k, v]) => <option key={k} value={k}>Priorité {v.toLowerCase()}</option>)}
        </select>
      </div>
      <textarea required rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Décrivez votre problème…" className={field} data-testid="portal-ticket-message" />
      <div className="flex gap-3 justify-end">
        <button type="button" onClick={onCancel} className="px-5 py-2.5 rounded-full text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
        <button type="submit" className="px-5 py-2.5 rounded-full bg-[#0F2A6B] text-white text-sm font-semibold hover:bg-[#0A1F52]" data-testid="portal-ticket-submit">Envoyer</button>
      </div>
    </form>
  );
}

function Thread({ ticket, onBack, onUpdate }) {
  const [text, setText] = useState("");
  const send = async () => {
    if (!text.trim()) return;
    try { const { data } = await portalApi.post(`/portal/tickets/${ticket.id}/messages`, { text }); setText(""); onUpdate(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200" data-testid="portal-ticket-thread">
      <div className="p-5 border-b border-slate-100 flex flex-wrap items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-full hover:bg-slate-100" data-testid="portal-ticket-back"><ArrowLeft className="w-4 h-4" /></button>
        <div className="flex-1 min-w-0"><div className="text-xs font-mono text-slate-400">{ticket.reference}</div><div className="font-semibold text-[#0F2A6B] truncate">{ticket.subject}</div></div>
        <Pill map={TICKET_STATUS} value={ticket.status} testid="portal-ticket-status" />
      </div>
      <div className="p-5 space-y-4 max-h-[460px] overflow-y-auto">
        {ticket.messages.map((m) => (
          <div key={m.id} className={`flex ${m.author_type === "client" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm ${m.author_type === "client" ? "bg-[#0F2A6B] text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
              <div className={`text-[11px] mb-1 ${m.author_type === "client" ? "text-slate-300" : "text-slate-500"}`}>{m.author_type === "client" ? "Vous" : `${m.author} · VISION LOGICIELS`} — {new Date(m.at).toLocaleString("fr-FR")}</div>
              <div className="whitespace-pre-wrap">{m.text}</div>
            </div>
          </div>
        ))}
      </div>
      {ticket.status !== "ferme" && (
        <div className="p-4 border-t border-slate-100 flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Votre message…" className={field} data-testid="portal-ticket-reply-input" />
          <button onClick={send} className="px-4 rounded-xl bg-[#4CB944] text-white hover:bg-[#3AA033]" data-testid="portal-ticket-reply-send"><Send className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

export function TicketsTab() {
  const [items, setItems] = useState(null);
  const [mode, setMode] = useState(null);
  const load = () => portalApi.get("/portal/tickets").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const upsert = (t) => { setMode(t); load(); };

  if (mode === "new") return <NewTicket onCreated={upsert} onCancel={() => setMode(null)} />;
  if (mode) return <Thread ticket={mode} onBack={() => setMode(null)} onUpdate={upsert} />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><button onClick={() => setMode("new")} data-testid="portal-ticket-new" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0F2A6B] text-white text-sm font-semibold hover:bg-[#0A1F52]"><Plus className="w-4 h-4" /> Nouveau ticket</button></div>
      {!items ? <div className="py-16 text-center text-slate-400">Chargement…</div> : !items.length ? <Empty text="Aucun ticket. Besoin d'aide ? Créez votre première demande." /> : (
        <div className="grid gap-3" data-testid="portal-tickets-list">
          {items.map((t) => (
            <button key={t.id} onClick={() => setMode(t)} data-testid={`portal-ticket-${t.id}`} className="text-left p-5 rounded-2xl bg-white border border-slate-200 hover:border-[#0F2A6B]/30 hover:shadow-md transition-[border-color,box-shadow] flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0"><div className="text-xs font-mono text-slate-400">{t.reference} · {fmtDate(t.updated_at)} · {t.messages.length} message(s)</div><div className="mt-1 font-semibold text-[#0F2A6B] truncate">{t.subject}</div></div>
              <Pill map={TICKET_STATUS} value={t.status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
